import { useState, useEffect } from 'react';
import moment from 'moment';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

type DateOption = {
  date: string;
  label: string;
  isOffDay: boolean;
  isLocked: boolean;
  isPausedDate?: boolean;
  platform?: string;
};

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  fullWidth?: boolean;
  // Date range configuration
  startDate?: string; // For end date selection, this limits the minimum date
  minDate?: string; // Minimum selectable date
  maxDate?: string; // Maximum selectable date
  maxDays?: number; // Maximum number of days to show (default: 30)
  defaultDaysOffset?: number; // Default selection offset from start (default: 0)
  // Server date and off days
  serverDate?: string;
  offDays?: number[]; // Array of day IDs (1=Sunday, 2=Monday, etc.)
  // For unpause functionality
  pausedDates?: Array<{
    date: string;
    platform?: string;
  }>;
  // Subscription information for unpause filtering
  subscriptionStartDate?: string; // Subscription start date for filtering logic
  // Styling options
  showPlatformChips?: boolean;
  showOffDayChips?: boolean;
  showLockedChips?: boolean;
  // Validation
  onlyAllowPausedDates?: boolean; // For unpause, only allow selection of paused dates
};

export default function CustomPauseUnpauseDateSelect({
  label,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  startDate,
  minDate,
  maxDate,
  maxDays = 30,
  defaultDaysOffset = 0,
  serverDate,
  offDays = [],
  pausedDates = [],
  subscriptionStartDate,
  showPlatformChips = false,
  showOffDayChips = true,
  showLockedChips = true,
  onlyAllowPausedDates = false,
}: Props) {
  const [dateOptions, setDateOptions] = useState<DateOption[]>([]);

  // Generate date options
  useEffect(() => {
    const generateOptions = () => {
      // Determine the base date for generating options
      let baseDate: moment.Moment;
      
      if (onlyAllowPausedDates && pausedDates.length > 0) {
        // For unpause, use the earliest paused date as base
        const earliestPausedDate = pausedDates.reduce((earliest, current) => {
          return moment(current.date).isBefore(earliest.date) ? current : earliest;
        });
        baseDate = moment(earliestPausedDate.date);
      } else if (startDate) {
        baseDate = moment(startDate);
      } else if (minDate) {
        baseDate = moment(minDate);
      } else {
        baseDate = moment(serverDate || moment().format('YYYY-MM-DD'));
      }
      
      const today = moment(serverDate || moment().format('YYYY-MM-DD'));
      const lockedDateThreshold = today.clone().add(2, 'days');
      
      const options: DateOption[] = [];
      const pausedDatesMap = new Map(pausedDates.map(pd => [pd.date, pd]));

      // For unpause mode, generate options only for paused dates
      if (onlyAllowPausedDates) {
        // Filter paused dates based on the business logic:
        // 1. If subscription start date is today or in the past: show paused dates >= today
        // 2. If subscription start date is in the future: show paused dates >= subscription start date
        // 
        // Example scenarios:
        // - Today: 2025-08-24, Subscription starts: 2025-08-27 → Show paused dates >= 2025-08-27
        // - Today: 2025-08-30, Subscription starts: 2025-08-27 → Show paused dates >= 2025-08-30
        const todayMoment = moment(serverDate || moment().format('YYYY-MM-DD'));
        const subscriptionStartMoment = subscriptionStartDate ? moment(subscriptionStartDate) : todayMoment;
        
        // Determine the minimum date to show based on the logic
        const minDateToShow = subscriptionStartMoment.isSameOrBefore(todayMoment) 
          ? todayMoment 
          : subscriptionStartMoment;
        
        pausedDates.forEach(pausedDate => {
          const currentDate = moment(pausedDate.date);
          const dateString = currentDate.format('YYYY-MM-DD');
          const dayOfWeek = currentDate.day();
          const dayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1;
          
          // Apply the business logic filter: only show dates >= minDateToShow
          if (currentDate.isBefore(minDateToShow)) {
            return; // Skip this date - it's before our minimum allowed date
          }
          
          // Apply maxDate filter if specified
          if (maxDate && currentDate.isAfter(maxDate)) {
            return; // Skip this date
          }
          
          const isOffDay = offDays.includes(dayId);
          const isLocked = currentDate.isSameOrBefore(lockedDateThreshold);
          
          const dayName = currentDate.format('ddd');
          const label = `${currentDate.format('DD MMM YYYY')} | ${dayName}`;
          
          options.push({
            date: dateString,
            label,
            isOffDay,
            isLocked,
            isPausedDate: true,
            platform: pausedDate.platform,
          });
        });
        
        // Sort options by date
        options.sort((a, b) => moment(a.date).diff(moment(b.date)));
      } else {
        // For pause mode, generate date range
        for (let i = 0; i < maxDays; i += 1) {
          const currentDate = baseDate.clone().add(i, 'day');
          const dateString = currentDate.format('YYYY-MM-DD');
          
          // Apply minDate filter
          if (minDate && currentDate.isBefore(minDate)) {
            continue;
          }
          
          // Apply maxDate filter
          if (maxDate && currentDate.isAfter(maxDate)) {
            break;
          }
          
          const dayOfWeek = currentDate.day();
          const dayId = dayOfWeek === 0 ? 1 : dayOfWeek + 1;
          
          const isOffDay = offDays.includes(dayId);
          const isLocked = currentDate.isSameOrBefore(lockedDateThreshold);
          const pausedDate = pausedDatesMap.get(dateString);
          const isPausedDate = Boolean(pausedDate);
          
          const dayName = currentDate.format('ddd');
          const label = `${currentDate.format('DD MMM YYYY')} | ${dayName}`;
          
          options.push({
            date: dateString,
            label,
            isOffDay,
            isLocked,
            isPausedDate,
            platform: pausedDate?.platform,
          });
        }
      }

      return options;
    };

    setDateOptions(generateOptions());
  }, [startDate, minDate, maxDate, maxDays, serverDate, offDays, pausedDates, subscriptionStartDate, onlyAllowPausedDates]);

  // Auto-select default option if no value is set
  useEffect(() => {
    if (!value && dateOptions.length > 0 && defaultDaysOffset >= 0 && !onlyAllowPausedDates) {
      // Only auto-select for pause mode, not unpause mode
      // Skip auto-selection if defaultDaysOffset is -1 (user should choose manually)
      
      // Find valid options (not off days, not locked if applicable)
      const validOptions = dateOptions.filter(opt => 
        !opt.isOffDay && 
        (!onlyAllowPausedDates || opt.isPausedDate)
      );
      
      if (validOptions.length === 0) {
        return; // No valid options available
      }
      
      // Try to find the option at the specified offset
      let defaultOption: DateOption | undefined;
      
      if (defaultDaysOffset < validOptions.length) {
        defaultOption = validOptions[defaultDaysOffset];
      } else {
        // If offset is beyond valid options, use the first valid option
        defaultOption = validOptions[0];
      }
      
      if (defaultOption) {
        onChange(defaultOption.date);
      }
    }
  }, [value, dateOptions, defaultDaysOffset, onChange, onlyAllowPausedDates]);



  return (
    <FormControl fullWidth={fullWidth} error={error} required={required}>
      <InputLabel id={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}>
        {label}
      </InputLabel>
      <Select
        labelId={`${label.toLowerCase().replace(/\s+/g, '-')}-label`}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        startAdornment={
          <InputAdornment position="start">
            <Iconify icon="eva:calendar-fill" />
          </InputAdornment>
        }
        renderValue={(selected) => {
          if (!selected) return '';
          const option = dateOptions.find(opt => opt.date === selected);
          return option ? option.label : selected;
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 400 },
          },
        }}
      >
        {dateOptions.length === 0 ? (
          <MenuItem value="">
            <em>Loading available dates...</em>
          </MenuItem>
        ) : (
          dateOptions.map((option) => {
            // Disable off days and handle unpause mode logic
            const isDisabled = option.isOffDay || (onlyAllowPausedDates ? !option.isPausedDate : false);
            
            return (
              <MenuItem
                key={option.date}
                value={option.date}
                disabled={isDisabled}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  minHeight: 48,
                  ...(option.isOffDay && {
                    opacity: 0.5,
                    fontStyle: 'italic',
                    color: 'text.disabled',
                  }),
                  ...(isDisabled && {
                    opacity: 0.5,
                  }),
                }}
              >
                <Box component="span" sx={{ flex: 1 }}>
                  {option.label}
                </Box>
                <Stack direction="row" spacing={0.5} sx={{ ml: 1 }}>
                {showLockedChips && option.isLocked && (
                    <Chip
                      label="Locked"
                      size="small"
                      color="warning"
                      variant="filled"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                  {showPlatformChips && option.isPausedDate && option.platform === 'client' && (
                    <Chip
                      label="Client"
                      size="small"
                      color="info"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                  {showPlatformChips && option.isPausedDate && option.platform === 'partners' && (
                    <Chip
                      label="Partners"
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                  {showOffDayChips && option.isOffDay && (
                    <Chip
                      label="Off"
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  )}
                </Stack>
              </MenuItem>
            );
          })
        )}
        {dateOptions.length === 0 && (
          <MenuItem value="" disabled>
            <em>No dates available</em>
          </MenuItem>
        )}
      </Select>
      {helperText && (
        <FormHelperText>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}